import { Action, combineReducers, Reducer } from '@reduxjs/toolkit';
import config from 'config';
import {
  createMigrate,
  type PersistedState,
  persistReducer
} from 'redux-persist';
import { addressesApi } from 'services/addressesApi';
import { bookingApi } from 'services/bookingApi';
import { locationsApi } from 'services/locationsApi';
import { packagesApi } from 'services/packagesApi';
import { paymentMethodsApi } from 'services/paymentMethodsApi';
import { productsApi } from 'services/productsApi';
import { shoppingCartApi } from 'services/shoppingCartApi';
import { siteAlertsApi } from 'services/siteAlert';
import { userProfileApi } from 'services/userProfileApi';

import { persistStorage as storage } from 'utils';
import {
  mapShopCartItemToPackage,
  mapShopCartItemToProduct
} from 'utils/cartMapping';
import {
  dedupeSuggestedServices,
  normalizeSuggestedServices
} from 'utils/helpers/suggestedServices';

import { SuggestedService } from 'types';
import { ShopCartItem } from 'types/cart.types';
import { ProductItem } from 'types/products.types';
import { ExtendedLocationPackageItem } from 'types/shop.types';

import bankReloadStore, { BankReloadStore } from './reducers/bankReloadSlice';
import bookingAppointmentStore, {
  BookingAppointmentStore
} from './reducers/bookingAppointmentSlice';
import cartStore, { CartStore } from './reducers/cartSlice';
import clientStore, { ClientStore } from './reducers/clientSlice';
import formStore, { FormStore } from './reducers/formSlice';
import giftCardsShopStore, {
  GiftCardsShopStore
} from './reducers/giftCardsShopSlice';
import giftCardsStore, { GiftCardsStore } from './reducers/giftCardsSlice';
import guestStore, {
  guestSessionFinished,
  GuestStore
} from './reducers/guestSlice';
import locationStore, { LocationsStore } from './reducers/locationSlice';
import membershipsStore, {
  MembershipsStore
} from './reducers/membershipsSlice';
import packagesShopStore, {
  PackagesShopStore
} from './reducers/packagesShopSlice';
import packagesStore, { PackagesStore } from './reducers/packagesSlice';
import persistAuthStore, { AuthStore } from './reducers/persistAuthSlice';
import persistGuestAuthStore, {
  GuestAuthStore
} from './reducers/persistGuestAuthSlice';
import productsStore, { ProductsStore } from './reducers/productsSlice';
import servicesStore, { ServicesStore } from './reducers/servicesSlice';
import userStore, {
  authSessionFinished,
  UserStore
} from './reducers/userSlice';
import { removeLoadingAndErrorsTransform } from './reduxTransforms';

export type RootStateType = {
  persistAuth: AuthStore;
  persistGuestAuth: GuestAuthStore;
  user: UserStore;
  guest: GuestStore;
  client: ClientStore;
  giftCards: GiftCardsStore;
  packages: PackagesStore;
  packagesShop: PackagesShopStore;
  giftCardsShop: GiftCardsShopStore;
  locations: LocationsStore;
  services: ServicesStore;
  bookingAppointment: BookingAppointmentStore;
  bankReload: BankReloadStore;
  cart: CartStore;
  products: ProductsStore;
  form: FormStore;
  memberships: MembershipsStore;
  siteAlerts: ReturnType<typeof siteAlertsApi.reducer>;
  locationsApi: ReturnType<typeof locationsApi.reducer>;
  productsApi: ReturnType<typeof productsApi.reducer>;
  userProfileApi: ReturnType<typeof userProfileApi.reducer>;
  shoppingCartApi: ReturnType<typeof shoppingCartApi.reducer>;
  addressesApi: ReturnType<typeof addressesApi.reducer>;
  packagesApi: ReturnType<typeof packagesApi.reducer>;
  bookingApi: ReturnType<typeof bookingApi.reducer>;
  paymentMethodsApi: ReturnType<typeof paymentMethodsApi.reducer>;
};

const combinedReducer: Reducer<RootStateType, Action> = combineReducers({
  [siteAlertsApi.reducerPath]: siteAlertsApi.reducer,
  [locationsApi.reducerPath]: locationsApi.reducer,
  [productsApi.reducerPath]: productsApi.reducer,
  [userProfileApi.reducerPath]: userProfileApi.reducer,
  [shoppingCartApi.reducerPath]: shoppingCartApi.reducer,
  [addressesApi.reducerPath]: addressesApi.reducer,
  [packagesApi.reducerPath]: packagesApi.reducer,
  [bookingApi.reducerPath]: bookingApi.reducer,
  [paymentMethodsApi.reducerPath]: paymentMethodsApi.reducer,
  persistAuth: persistAuthStore,
  persistGuestAuth: persistGuestAuthStore,
  user: userStore,
  guest: guestStore,
  client: clientStore,
  giftCards: giftCardsStore,
  packages: packagesStore,
  packagesShop: packagesShopStore,
  giftCardsShop: giftCardsShopStore,
  locations: locationStore,
  services: servicesStore,
  bookingAppointment: bookingAppointmentStore,
  bankReload: bankReloadStore,
  cart: cartStore,
  products: productsStore,
  form: formStore,
  memberships: membershipsStore
});

// TODO: Define the current version - increment this when you need new migrations
const NEW_VERSION = 2;
// Scope the persisted store to the active client so switching clients (which
// share the same origin/localStorage in dev and multi-tenant setups) can never
// reuse another client's auth token/clid against the wrong backend.
const CLIENT_KEY = String(config?.name ?? 'default')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9-]+/g, '-');
const PERSIST_KEY = `root-${CLIENT_KEY}`;

const migrations = {
  0: (state: PersistedState): PersistedState => {
    const s = state as RootStateType & PersistedState;
    return {
      ...s,
      cart: {
        ...s.cart,
        fulfillmentPreferences: s.cart?.fulfillmentPreferences || {},
        isPreferencesMenuEnabled: s.cart?.isPreferencesMenuEnabled ?? true,
        currentInvoiceId: s.cart?.currentInvoiceId || null
      }
    } as PersistedState;
  },
  1: (state: PersistedState): PersistedState => {
    const s = state as RootStateType & PersistedState;
    const services = s.services as ServicesStore & {
      serviceRecommendations?: Record<number, unknown[]>;
      clientRecommendations?: SuggestedService[];
      clientRecommendationsFetched?: boolean;
    };

    const legacyRecommendations = services?.serviceRecommendations
      ? Object.values(services.serviceRecommendations).flat()
      : [];

    const mergedSuggestions = dedupeSuggestedServices(
      normalizeSuggestedServices([
        ...(services?.clientRecommendations ?? []),
        ...legacyRecommendations
      ])
    );

    return {
      ...s,
      services: {
        ...services,
        clientRecommendations: mergedSuggestions,
        clientRecommendationsFetched:
          services?.clientRecommendationsFetched ?? false
      }
    } as PersistedState;
  },
  2: (state: PersistedState): PersistedState => {
    const s = state as RootStateType & PersistedState;
    const services = s.services as ServicesStore & {
      clientRecommendations?: SuggestedService[];
      clientRecommendationsFetched?: boolean;
    };

    return {
      ...s,
      services: {
        ...services,
        suggestedServices: dedupeSuggestedServices(
          (
            services?.suggestedServices ??
            services?.clientRecommendations ??
            []
          ).map((item) => ({
            ...item,
            parent_svc_id:
              item.parent_svc_id ??
              (item as SuggestedService & { svc_id?: number }).svc_id ??
              0
          }))
        ).filter((item) => item.parent_svc_id > 0),
        suggestedServicesFetched:
          services?.suggestedServicesFetched ??
          services?.clientRecommendationsFetched ??
          false,
        isSuggestedServicesLoading: false,
        suggestedServicesFetchKey: services?.suggestedServicesFetchKey ?? ''
      }
    } as PersistedState;
  }
};

const persistConfig = {
  key: PERSIST_KEY,
  storage,
  version: NEW_VERSION,
  whitelist: [
    'persistAuth',
    'persistGuestAuth',
    'user',
    'guest',
    'cart',
    'bookingAppointment',
    'giftCardsShop',
    'services',
    'memberships',
    // 'cards',
    'siteAlertsApi'
  ],
  transforms: [removeLoadingAndErrorsTransform],
  migrate: createMigrate(migrations, {
    debug: process.env.NODE_ENV === 'development'
  })
};

const rootReducer: Reducer<RootStateType, Action> = (
  state: RootStateType | undefined,
  action: Action
) => {
  const isUserLogOutActionType = authSessionFinished.match(action);
  const isGuestLogOutActionType = guestSessionFinished.match(action);

  if (isUserLogOutActionType) {
    storage.removeItem(`persist:${PERSIST_KEY}`);
    return combinedReducer(undefined, action);
  }

  if (isGuestLogOutActionType && state) {
    const backendCart =
      state.shoppingCartApi.queries['getShoppingCart("")']?.data;
    let selectedProducts: ProductItem[] = [];
    let selectedPackages: ExtendedLocationPackageItem[] = [];

    if ((backendCart as { items?: ShopCartItem[] })?.items) {
      const { items } = backendCart as { items: ShopCartItem[] };
      selectedProducts = items
        .filter((i) => i.item_type === 'P')
        .map(mapShopCartItemToProduct);
      selectedPackages = items
        .filter((i) => i.item_type === 'K')
        .map(mapShopCartItemToPackage);
    }
    const newState = {
      ...state,
      cart: {
        ...state.cart,
        selectedProducts,
        selectedPackages,
        cartStep: 0
      },
      guest: guestStore(undefined, action),
      persistGuestAuth: persistGuestAuthStore(undefined, action)
    };
    return combinedReducer(newState, action);
  }

  return combinedReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export default persistedReducer;
